import { CommonModule, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzSelectModule } from 'ng-zorro-antd/select';

import { Customer360Service } from './services/customer-360.service';
import { CustomerCustomFieldsService } from './services/customer-custom-fields.service';
import { VisitPhotosService } from './services/visit-photos.service';
import {
  Customer360Bundle,
  Customer360DrawerData,
  CustomerCustomField,
  VisitPhoto,
} from './models/customer-360.types';
import { G2ChartComponent } from './components/g2-chart/g2-chart.component';

/**
 * Customer 360 drawer.
 *
 * One host component that loads the `/reports/customers/:id/360` bundle
 * once on open and renders seven stacked panels against it. The drawer
 * is opened via `NzDrawerService.create({ nzContent: Customer360Drawer,
 * nzData: { userId } })`.
 *
 * Panels (top → bottom):
 *  1. Header KPIs         - totalVisits, totalSpent, avgCheck, lastVisit, balance
 *  2. Visit timeline      - recent visits with status + services
 *  3. Analytics charts    - 6 g2plot charts in a responsive 2-col grid
 *  4. General info        - editable key/value custom fields (bulk save)
 *  5. Photo gallery       - drag-drop uploads + grid preview
 *  6. Financial summary   - spending series + totals
 *  7. Communication log   - SMS + email events (sorted desc)
 *
 * State is drawer-scoped with Angular signals — no NGXS slice, because
 * the drawer lifetime is <1 minute and nothing else in the app needs to
 * observe it.
 */
@Component({
  selector: 'beautyapp-customer-360-drawer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    DecimalPipe,
    NzSpinModule,
    NzEmptyModule,
    NzTagModule,
    NzAvatarModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzImageModule,
    NzSelectModule,
    G2ChartComponent,
  ],
  template: `
    <nz-spin [nzSpinning]="loading()" nzSize="large">
      <div class="c360" *ngIf="bundle() as b; else emptyTpl">

        <!-- 1. Header KPIs -->
        <section class="panel kpis">
          <div class="name-line">
            <nz-avatar [nzText]="initials()" nzSize="large"></nz-avatar>
            <div>
              <h2>{{ drawerData.userFullName || '—' }}</h2>
              <div class="mobile">{{ drawerData.userMobile || '' }}</div>
            </div>
            <button nz-button nzType="default" (click)="close()">
              <span nz-icon nzType="close"></span>
            </button>
          </div>
          <div class="kpi-grid">
            <div class="kpi">
              <div class="label">სულ ვიზიტი</div>
              <div class="value">{{ b.summary.totalVisits }}</div>
            </div>
            <div class="kpi">
              <div class="label">სულ დახარჯული</div>
              <div class="value">{{ b.summary.totalSpent | number: '1.0-2' }} ₾</div>
            </div>
            <div class="kpi">
              <div class="label">საშ. ჩეკი</div>
              <div class="value">{{ b.summary.averageCheck | number: '1.0-2' }} ₾</div>
            </div>
            <div class="kpi">
              <div class="label">ბოლო ვიზიტი</div>
              <div class="value">{{ b.summary.lastVisit || '—' }}</div>
            </div>
            <div class="kpi">
              <div class="label">პირველი ვიზიტი</div>
              <div class="value">{{ b.summary.firstVisit || '—' }}</div>
            </div>
            <div class="kpi">
              <div class="label">ბალანსი</div>
              <div class="value" [class.neg]="b.summary.balance < 0">
                {{ b.summary.balance | number: '1.0-2' }} ₾
              </div>
            </div>
          </div>
        </section>

        <!-- 2. Visit timeline -->
        <section class="panel">
          <h3>ვიზიტების ისტორია</h3>
          <div *ngIf="b.recentVisits?.length; else emptyVisits" class="timeline">
            <div class="tl-row" *ngFor="let v of b.recentVisits">
              <div class="tl-date">{{ v.date }}</div>
              <div class="tl-body">
                <nz-tag [nzColor]="statusColor(v.status)">{{ statusLabel(v.status) }}</nz-tag>
                <span class="tl-emp">{{ v.employeeName || '—' }}</span>
                <span class="tl-svc">
                  <ng-container *ngFor="let s of v.services; let last = last">
                    {{ s.serviceName }}<ng-container *ngIf="!last">, </ng-container>
                  </ng-container>
                </span>
              </div>
              <div class="tl-total">{{ v.total | number: '1.0-2' }} ₾</div>
            </div>
          </div>
          <ng-template #emptyVisits><nz-empty></nz-empty></ng-template>
        </section>

        <!-- 3. Analytics charts -->
        <section class="panel">
          <h3>ანალიტიკა</h3>
          <div class="chart-grid">
            <div class="chart-card">
              <div class="chart-title">კვირის დღის მიხედვით</div>
              <beautyapp-g2-chart
                type="column"
                [data]="dayOfWeekData()"
                [config]="{ xField: 'label', yField: 'count', columnWidthRatio: 0.6 }"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">საათის მიხედვით</div>
              <beautyapp-g2-chart
                type="column"
                [data]="hourData()"
                [config]="{ xField: 'hour', yField: 'count' }"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">ბოლო 12 თვე — ვიზიტები</div>
              <beautyapp-g2-chart
                type="line"
                [data]="monthData()"
                [config]="{ xField: 'month', yField: 'visits', smooth: true }"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">ხარჯი დროში</div>
              <beautyapp-g2-chart
                type="dual"
                [data]="spendingDual()"
                [config]="spendingDualCfg"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">თანამშრომლების მიხედვით</div>
              <beautyapp-g2-chart
                type="bar"
                [data]="employeeData()"
                [config]="{ xField: 'visits', yField: 'employeeFullName' }"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">ტოპ სერვისები</div>
              <beautyapp-g2-chart
                type="pie"
                [data]="servicesData()"
                [config]="{ angleField: 'count', colorField: 'serviceName', radius: 0.8, innerRadius: 0.5 }"
              ></beautyapp-g2-chart>
            </div>
          </div>
        </section>

        <!-- 4. General info (custom fields) -->
        <section class="panel">
          <h3>დამატებითი ინფორმაცია</h3>
          <div class="cf-list">
            <div class="cf-row" *ngFor="let f of customFields(); let i = index; trackBy: trackCf">
              <input
                nz-input
                placeholder="გასაღები (მაგ. თმის ფორმულა)"
                [(ngModel)]="f.fieldKey"
                (ngModelChange)="markDirty()"
              />
              <input
                nz-input
                placeholder="მნიშვნელობა"
                [ngModel]="f.fieldValue"
                (ngModelChange)="setCfValue(i, $event)"
              />
              <button nz-button nzDanger nzType="text" (click)="removeCf(i)">
                <span nz-icon nzType="delete"></span>
              </button>
            </div>
            <button nz-button nzType="dashed" (click)="addCf()">
              <span nz-icon nzType="plus"></span> დამატება
            </button>
          </div>
          <div class="cf-actions" *ngIf="cfDirty()">
            <button nz-button nzType="primary" [nzLoading]="savingCf()" (click)="saveCf()">
              შენახვა
            </button>
          </div>
        </section>

        <!-- 5. Photo gallery -->
        <section class="panel">
          <h3>ფოტოები</h3>
          <div class="photo-uploader">
            <!-- Backend requires each photo to belong to a specific
                 booking or visit (CHECK constraint on visit_photo), so
                 the gallery asks the user to pick a visit from the
                 recent-visits list before enabling the file picker. -->
            <nz-select
              [ngModel]="photoTargetBookingId()"
              (ngModelChange)="photoTargetBookingId.set($event)"
              nzPlaceHolder="ვიზიტის არჩევა ატვირთვისთვის"
              style="min-width: 260px"
            >
              <nz-option
                *ngFor="let v of b.recentVisits"
                [nzValue]="v.bookingId"
                [nzLabel]="v.date + ' — ' + (v.employeeName || '')"
              ></nz-option>
            </nz-select>
            <button
              nz-button
              (click)="fileInput.click()"
              [nzLoading]="uploadingPhoto()"
              [disabled]="!photoTargetBookingId()"
            >
              <span nz-icon nzType="upload"></span> ატვირთვა
            </button>
            <input
              #fileInput
              type="file"
              accept="image/*"
              multiple
              style="display:none"
              (change)="onPhotoFiles($event)"
            />
          </div>

          <div class="photo-grid" *ngIf="photos().length; else emptyPhotos">
            <div class="photo" *ngFor="let p of photos()">
              <img nz-image [nzSrc]="p.url" [alt]="p.caption || ''" />
              <div class="caption">{{ p.caption }}</div>
            </div>
          </div>
          <ng-template #emptyPhotos><nz-empty></nz-empty></ng-template>
        </section>

        <!-- 6. Financial summary -->
        <section class="panel">
          <h3>ფინანსური რეზიუმე</h3>
          <div class="kpi-grid">
            <div class="kpi">
              <div class="label">სულ დახარჯული</div>
              <div class="value">{{ b.spending.totalSpent | number: '1.0-2' }} ₾</div>
            </div>
            <div class="kpi">
              <div class="label">საშ. ჩეკი</div>
              <div class="value">{{ b.spending.averageCheck | number: '1.0-2' }} ₾</div>
            </div>
            <div class="kpi">
              <div class="label">მიმდინარე ბალანსი</div>
              <div class="value" [class.neg]="b.summary.balance < 0">
                {{ b.summary.balance | number: '1.0-2' }} ₾
              </div>
            </div>
          </div>
        </section>

        <!-- 7. Communication log -->
        <section class="panel">
          <h3>კომუნიკაცია</h3>
          <div *ngIf="b.communications?.length; else emptyComms" class="comm-list">
            <div class="comm-row" *ngFor="let c of b.communications">
              <span nz-icon [nzType]="c.channel === 'sms' ? 'mobile' : 'mail'"></span>
              <div class="comm-body">
                <div class="comm-head">
                  <span class="comm-date">{{ c.sentAt }}</span>
                  <nz-tag [nzColor]="commStatusColor(c.status)">{{ c.status || '—' }}</nz-tag>
                  <span class="comm-event" *ngIf="c.eventType">{{ c.eventType }}</span>
                </div>
                <div class="comm-text">{{ c.subject || c.preview }}</div>
              </div>
            </div>
          </div>
          <ng-template #emptyComms><nz-empty></nz-empty></ng-template>
        </section>
      </div>
      <ng-template #emptyTpl>
        <div *ngIf="!loading()"><nz-empty></nz-empty></div>
      </ng-template>
    </nz-spin>
  `,
  styles: [`
    .c360 { padding: 0 4px 32px; display: flex; flex-direction: column; gap: 20px; }
    .panel { background: #fff; border: 1px solid #f0f0f0; border-radius: 8px; padding: 20px; }
    .panel h3 { margin: 0 0 16px; font-size: 16px; font-weight: 600; }
    .kpis { position: sticky; top: 0; z-index: 2; }
    .name-line { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
    .name-line h2 { margin: 0; font-size: 20px; }
    .name-line .mobile { color: #8c8c8c; font-size: 13px; }
    .name-line button { margin-left: auto; }
    .kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 12px; }
    .kpi { background: #fafafa; border-radius: 6px; padding: 12px; }
    .kpi .label { color: #8c8c8c; font-size: 12px; }
    .kpi .value { font-size: 18px; font-weight: 600; margin-top: 4px; }
    .kpi .value.neg { color: #f5222d; }
    .timeline { display: flex; flex-direction: column; gap: 8px; }
    .tl-row { display: grid; grid-template-columns: 120px 1fr auto; gap: 12px; padding: 8px 0; border-bottom: 1px dashed #f0f0f0; align-items: center; }
    .tl-row:last-child { border-bottom: none; }
    .tl-date { color: #595959; font-size: 13px; }
    .tl-body { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .tl-emp { font-weight: 500; }
    .tl-svc { color: #8c8c8c; }
    .tl-total { font-weight: 600; text-align: right; }
    .chart-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .chart-card { background: #fafafa; border-radius: 6px; padding: 12px; min-height: 280px; display: flex; flex-direction: column; }
    .chart-title { font-weight: 600; margin-bottom: 8px; color: #595959; }
    @media (max-width: 900px) { .chart-grid { grid-template-columns: 1fr; } }
    .cf-list { display: flex; flex-direction: column; gap: 8px; }
    .cf-row { display: grid; grid-template-columns: 1fr 2fr auto; gap: 8px; align-items: center; }
    .cf-actions { margin-top: 12px; text-align: right; }
    .photo-uploader { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; flex-wrap: wrap; }
    .photo-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px; }
    .photo { border-radius: 6px; overflow: hidden; }
    .photo img { width: 100%; height: 140px; object-fit: cover; }
    .photo .caption { font-size: 12px; color: #8c8c8c; padding: 4px 6px; }
    .comm-list { display: flex; flex-direction: column; gap: 10px; }
    .comm-row { display: grid; grid-template-columns: 24px 1fr; gap: 10px; padding: 8px 0; border-bottom: 1px dashed #f0f0f0; }
    .comm-row:last-child { border-bottom: none; }
    .comm-head { display: flex; gap: 8px; align-items: center; font-size: 12px; color: #8c8c8c; }
    .comm-text { margin-top: 2px; }
  `],
})
export class Customer360Drawer {
  drawerData = inject<Customer360DrawerData>(NZ_DRAWER_DATA);
  private drawerRef = inject<NzDrawerRef<unknown, unknown>>(NzDrawerRef);
  private api = inject(Customer360Service);
  private cfApi = inject(CustomerCustomFieldsService);
  private photosApi = inject(VisitPhotosService);
  private msg = inject(NzMessageService);

  loading = signal(true);
  bundle = signal<Customer360Bundle | null>(null);
  photos = signal<VisitPhoto[]>([]);
  customFields = signal<CustomerCustomField[]>([]);
  cfDirty = signal(false);
  savingCf = signal(false);
  uploadingPhoto = signal(false);
  photoTargetBookingId = signal<string | null>(null);

  // Derived chart data ------------------------------------------------------
  dayOfWeekData = computed(() => this.bundle()?.byDayOfWeek ?? []);
  hourData = computed(() => this.bundle()?.byHourOfDay ?? []);
  monthData = computed(() => this.bundle()?.byMonth ?? []);
  employeeData = computed(() => this.bundle()?.byEmployee ?? []);
  servicesData = computed(() => this.bundle()?.services ?? []);

  /**
   * g2plot DualAxes expects `data: [visitSeries, spendSeries]` with a
   * shared xField. We pivot the single `SpendingPoint[]` into that
   * shape at render time so the backend stays flat.
   */
  spendingDual = computed(() => {
    const series = this.bundle()?.spending?.series ?? [];
    return [series, series];
  });

  spendingDualCfg = {
    xField: 'period',
    yField: ['spent', 'visits'],
    geometryOptions: [
      { geometry: 'line', smooth: true },
      { geometry: 'line', smooth: true },
    ],
  };

  initials = computed(() => {
    const name = this.drawerData.userFullName || '';
    return name
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  });

  constructor() {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.api.get360(this.drawerData.userId).subscribe({
      next: (b: Customer360Bundle) => {
        this.bundle.set(b);
        this.photos.set(b.photos ?? []);
        this.customFields.set(
          (b.customFields ?? []).map((f: CustomerCustomField) => ({ ...f })),
        );
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.msg.error('მონაცემების ჩატვირთვა ვერ მოხერხდა');
      },
    });
  }

  close(): void {
    this.drawerRef.close();
  }

  // Visit status mapping ----------------------------------------------------
  statusLabel(s: number): string {
    switch (s) {
      case 1: return 'დაგეგმილი';
      case 2: return 'დასრულებული';
      case 3: return 'გაუქმებული';
      case 4: return 'არ გამოცხადდა';
      default: return '—';
    }
  }
  statusColor(s: number): string {
    switch (s) {
      case 1: return 'blue';
      case 2: return 'green';
      case 3: return 'default';
      case 4: return 'red';
      default: return 'default';
    }
  }
  commStatusColor(s: string | null): string {
    if (!s) return 'default';
    const up = s.toUpperCase();
    if (up.includes('DELIVERED') || up.includes('OPENED') || up.includes('SENT')) return 'green';
    if (up.includes('FAILED') || up.includes('ERROR')) return 'red';
    return 'blue';
  }

  // Custom fields editing ---------------------------------------------------
  trackCf(_: number, f: CustomerCustomField) { return f.id ?? f.fieldKey; }
  markDirty() { this.cfDirty.set(true); }
  setCfValue(i: number, value: string) {
    const arr = [...this.customFields()];
    arr[i] = { ...arr[i], fieldValue: value };
    this.customFields.set(arr);
    this.cfDirty.set(true);
  }
  addCf() {
    this.customFields.set([
      ...this.customFields(),
      { id: 0, fieldKey: '', fieldValue: '' },
    ]);
    this.cfDirty.set(true);
  }
  removeCf(i: number) {
    const arr = [...this.customFields()];
    arr.splice(i, 1);
    this.customFields.set(arr);
    this.cfDirty.set(true);
  }
  saveCf(): void {
    const payload = this.customFields()
      .filter((f) => f.fieldKey.trim())
      .map((f) => ({ fieldKey: f.fieldKey.trim(), fieldValue: f.fieldValue }));
    this.savingCf.set(true);
    this.cfApi.bulkReplace(this.drawerData.userId, payload).subscribe({
      next: (rows: CustomerCustomField[]) => {
        this.customFields.set(rows.map((r: CustomerCustomField) => ({ ...r })));
        this.cfDirty.set(false);
        this.savingCf.set(false);
        this.msg.success('შენახულია');
      },
      error: () => {
        this.savingCf.set(false);
        this.msg.error('შენახვა ვერ მოხერხდა');
      },
    });
  }

  // Photo upload ------------------------------------------------------------
  /**
   * Plain `<input type="file">` handler. We avoid `nz-upload` here
   * because it wants a URL-based action or a Subscription-returning
   * custom request, and both shapes complicate error handling for
   * multi-file drops. A native input sends files one at a time and
   * lets us use our typed `ApiService`-based `VisitPhotosService`.
   */
  onPhotoFiles(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    const bookingId = this.photoTargetBookingId();
    if (!bookingId) {
      this.msg.warning('აირჩიე ვიზიტი');
      input.value = '';
      return;
    }
    this.uploadingPhoto.set(true);
    let pending = files.length;
    for (const file of files) {
      this.photosApi.upload(file, { bookingId }).subscribe({
        next: (p: VisitPhoto) => {
          this.photos.set([...this.photos(), p]);
          if (--pending === 0) {
            this.uploadingPhoto.set(false);
            this.msg.success('ატვირთულია');
          }
        },
        error: () => {
          if (--pending === 0) this.uploadingPhoto.set(false);
          this.msg.error('ატვირთვა ვერ მოხერხდა');
        },
      });
    }
    input.value = '';
  }
}
